import React from 'react';

import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM } from '../../util/types';
import { constructQueryParamName } from './SearchPage.shared';
import SelectSingleFilter from './SelectSingleFilter/SelectSingleFilter';
import SelectMultipleFilter from './SelectMultipleFilter/SelectMultipleFilter';
import KeywordFilter from './KeywordFilter/KeywordFilter';
import PriceFilter from './PriceFilter/PriceFilter';

import { currenciesHelper as currency } from '../../helpers/currencies';
import { defaultCurrency, currencyOptions } from '../../util/currency';

import { languagesHelper as language } from '../../helpers/languages';

// Helper: get enumOptions in a format that works as query parameter
const createFilterOptions = options =>
  options.map(o => ({ key: `${o.option}`, label: o.label, isFirst: o.isFirst || false }));

/**
 * FilterComponent is used to map configured filter types
 * to actual filter components
 */
const FilterComponent = props => {
  const {
    idPrefix,
    config,
    urlQueryParams,
    initialValues,
    getHandleChangedValueFn,
    marketplaceCurrency,
    intl,
    conversionRates,
    ...rest
  } = props;
  // Note: config can be either
  // - listingFields config or
  // - default filter config
  // They both have 'key' and 'schemaType' included.
  const { key, schemaType } = config;
  const { liveEdit, showAsPopup } = rest;

  const useHistoryPush = liveEdit || showAsPopup;
  const prefix = idPrefix || 'SearchPage';
  const componentId = `${prefix}.${key.toLowerCase()}`;
  const name = key.replace(/\s+/g, '-').toLowerCase();

  const filtersWithSearchInput = [];

  // Default filters: price, keywords, dates
  switch (key) {
    case 'price': {
      const { min, max, step } = config;

      let currentCurrency = currency.get();
      let currentCurrencyLabel = currencyOptions?.find(c => c.key === currentCurrency)?.label;
      const isOtherCurrency = !!conversionRates && defaultCurrency !== currentCurrency;
      const maxLimit =
        conversionRates && !!Object.values(conversionRates)?.length
          ? currencyOptions?.find(c => c.key === currentCurrency)?.maxLimit
          : max;

      return (
        <PriceFilter
          id={componentId}
          label={intl.formatMessage({ id: 'FilterComponent.priceLabel' })}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          min={min}
          max={maxLimit}
          step={step}
          marketplaceCurrency={marketplaceCurrency}
          conversionRates={conversionRates}
          isOtherCurrency={isOtherCurrency}
          currentCurrency={currentCurrency}
          currentCurrencyLabel={currentCurrencyLabel}
          {...rest}
        />
      );
    }
    case 'keywords':
      return (
        <KeywordFilter
          id={componentId}
          label={intl.formatMessage({ id: 'FilterComponent.keywordsLabel' })}
          name={name}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          {...rest}
        />
      );
  }

  // Custom extended data filters
  switch (schemaType) {
    case SCHEMA_TYPE_ENUM: {
      const { scope, enumOptions, filterConfig = {} } = config;
      const searchMode = 'has_any';
      const queryParamNames = [constructQueryParamName(key, scope)];
      const withSearchInput = filtersWithSearchInput.includes(key);

      return filterConfig.filterType === 'SelectSingleFilter' ? (
        <SelectSingleFilter
          id={componentId}
          label={language.labelsTranslator(key, intl) || filterConfig.label}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSelect={getHandleChangedValueFn(useHistoryPush)}
          options={createFilterOptions(enumOptions)}
          {...rest}
        />
      ) : (
        <SelectMultipleFilter
          id={componentId}
          label={language.labelsTranslator(key, intl) || filterConfig.label}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={createFilterOptions(enumOptions)}
          schemaType={schemaType}
          searchMode={searchMode}
          withSearchInput={withSearchInput}
          {...rest}
        />
      );
    }
    case SCHEMA_TYPE_MULTI_ENUM: {
      const { scope, enumOptions, filterConfig = {} } = config;
      const { label } = filterConfig;
      const searchMode = 'has_any';
      const queryParamNames = [constructQueryParamName(key, scope)];
      const withSearchInput = filtersWithSearchInput.includes(key);

      return (
        <SelectMultipleFilter
          id={componentId}
          label={language.labelsTranslator(key, intl) || label}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={createFilterOptions(enumOptions)}
          schemaType={schemaType}
          searchMode={searchMode}
          withSearchInput={withSearchInput}
          {...rest}
        />
      );
    }
    default:
      return null;
  }
};

export default FilterComponent;
